import { useRef } from 'react';
import './SyncToCalDialog.scss';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import google from '../../assets/images/google.png';
// import apple from '../../assets/images/apple.png';
import outlook from '../../assets/images/outlook.png';
import { useCallback, useEffect, useState } from 'react';
import SyncAddAccountDialog from '../SyncAddAccountDialog/SyncAddAccountDialog';
import AcDialog from '../AcDialog/AcDialog';
import SyncDialogModal from '../SyncDialogModal/SyncDialogModal';
import { shareEventsColumns, getNickNamesFromAccounts, getAvailableEmailIdsFromAccounts } from '../SyncAddAccountDialog/SyncToCalendarHelper';
import { Link } from "react-router-dom";
import { useAuthenticationContext } from '../../providers/AuthenticationProvider';
import { useSyncToCalendarProvider } from '../../providers/SyncToCalendarProvider';
import { useSettingsPageProvider } from '../../providers/SettingsProvider';
import { validateiOSAccountDetails, handleEmailValidation, handlePwdValidation } from '../SyncAddAccountDialog/iOSAuthenticateHelper';

const SyncToCalDialog = ({ data, open, onClose }) => {
    const { t } = useTranslation();
    const syncToCalendarContext = useSyncToCalendarProvider();
    const [syncToConsentOpen, setSyncToConsentOpen] = useState(false);
    const [syncAddAccountOpen, setSyncAddAccountOpen] = useState(false);
    const [syncToInstructionOpen, setSyncToInstructionOpen] = useState(false);
    const [addAccountCloseModal, setAddAccountCloseModal] = useState(false);
    const [modalFlag, setModalFlag] = useState({ iOSFlag: false, googleFlag: false, outlookFlag: false });
    const [checked, setChecked] = useState(false);
    const [synciOSLoginOpen, setSynciOSLoginOpen] = useState(false);
    const [emailId, setEmailId] = useState('');
    const [appleSpecificPassword, setAppleSpecificPassword] = useState('');
    const [shareEventsCol, setShareEventsCol] = useState<any>({});
    const [confirmCloseModal, setConfirmCloseModal] = useState(false);
    const { signIn, authCode, isGoogleSigned, resetIsGoogleSigned, resetIsOutlookSigned, isOutlookSigned, outlookSync } = useAuthenticationContext();
    const [emailErrorMsg, setEmailErrorMsg] = useState({ invalidError: false, alreadyExistsError: false });
    const [pwdErrorMsg, setPwdErrorMsg] = useState(false);
    // const [googleText, setGoogleText] = useState(false);
    const [enableEmailAddress, setEnableEmailAddress] = useState(false);
    const [token, setToken] = useState<String | null>('');
    const [accountNickNames, setAccountNickNames] = useState([]);
    const [availableEmailIds, setAvailableEmails] = useState(data?.availableEmailIds || {});
    const settingsContext = useSettingsPageProvider();
    const googleSignInRef = useRef(false);

    useEffect(() => {
        if (isGoogleSigned && googleSignInRef.current) {
            // wait for the google login
            googleSignInRef.current = false;
            setSyncAddAccountOpen(true);
            setToken(authCode);
            resetIsGoogleSigned();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGoogleSigned]);

    useEffect(() => {
        if (!accountNickNames.length || !data?.settingsScreen) {
            const syncedAccounts = getNickNamesFromAccounts(settingsContext?.syncedCalendarData);
            setAccountNickNames(syncedAccounts);
        }
        if (!availableEmailIds.length || !data?.settingsScreen) {
            const emailListByType = getAvailableEmailIdsFromAccounts(settingsContext?.syncedCalendarData);
            setAvailableEmails(emailListByType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsContext?.syncedCalendarData, data?.settingsScreen]);

    useEffect(() => {
        if (isOutlookSigned) {
            setEnableEmailAddress(true);
            setSyncAddAccountOpen(true);
        }
        else{
            setEnableEmailAddress(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOutlookSigned]);

    useEffect(() => {
        if (modalFlag.googleFlag) setEnableEmailAddress(true);
        else setEnableEmailAddress(false);

    }, [modalFlag.googleFlag]);

    const handleCheck = () => {
        setChecked((prev) => !prev);
    };

    //common for all modals - to handle close/cancel
    const handleClose = () => {
        setSyncToConsentOpen(false);
        setSyncToInstructionOpen(false);
        setSynciOSLoginOpen(false);
        resetIsGoogleSigned();
        resetIsOutlookSigned();
        resetAppleFields();
        // clearing old token
        setToken('');
    }

    //* to set the outlook auth code in token prop
    const receivedTokenSuccess = (authCode) => {
        if (!authCode) return;
        setToken(authCode);
    }

    //* Sync To Calendar continue btn for adding a account
    //* the flow when user click either Google, Outlook or Apple icon to continue with its flow
    const handleSyncContinueBtn = () => {
        handleClose();
        if (modalFlag.googleFlag) {
            googleSignInRef.current = true;
            signIn();
        }
        
        if (modalFlag.outlookFlag) {  
            outlookSync(receivedTokenSuccess);
        }

        if (modalFlag.iOSFlag) {
            window.open("https://appleid.apple.com");
            setSyncAddAccountOpen(false);
            setSynciOSLoginOpen(true);
        }
    }

    const handleAuthorizeRequestBtn = useCallback(async () => {
        const response = await validateiOSAccountDetails(emailId, appleSpecificPassword, syncToCalendarContext);
        const responseCode = response['errorCode'];
        const status = response['status'];
        if (status) {
            setToken(appleSpecificPassword);
            setSynciOSLoginOpen(false);
            setSyncAddAccountOpen(true);
        }
        else {
            responseCode && handleEmailValidation(responseCode, setEmailErrorMsg);
            responseCode && handlePwdValidation(responseCode, setPwdErrorMsg);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailId, appleSpecificPassword]);

    // ODP-539 hiding apple stuff*
    // iOS flow 
    // const openiOSInstructionPopUp = () => {
    //     setShareEventsCol({ ...shareEventsColumns });
    //     setSyncToInstructionOpen(true);
    //     setChecked(false);
    //     data.handleAction()
    // }

    const handleSyncStartSetup = () => {
        handleClose();
        setModalFlag({ iOSFlag: true, googleFlag: false, outlookFlag: false });
        setSyncToConsentOpen(true);
    }

    //iOS flow Instruction Data
    const syncToInstructionData = {
        title: t('myBlockMonth.syncToCalendar.syncToiOSCalendar'),
        titleSub: t('myBlockMonth.syncToCalendar.syncToiOSCalendarInstr'),
        actionLabel: t('myBlockMonth.syncToCalendar.syncCancel'),
        handleAction: handleClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.startSetup'),
        handleSecondaryAction: handleSyncStartSetup,
        modalInstruction: true,
    }

    //google flow 
    const openGoogleConsentPopUp = () => {
        setShareEventsCol({ ...shareEventsColumns });
        setModalFlag({ iOSFlag: false, googleFlag: true, outlookFlag: false });
        setChecked(false);
        setSyncToConsentOpen(true);
        data.handleAction();
    }

    //outlook flow 
    const openOutlookConsentPopUp = () => {
        setShareEventsCol({ ...shareEventsColumns });
        setModalFlag({ iOSFlag: false, googleFlag: false, outlookFlag: true });
        setChecked(false);
        setSyncToConsentOpen(true);
        data.handleAction();
    }

    //Add Account Close Action
    const handleAddAccountClose = () => {
        setSyncAddAccountOpen(false);
        setAddAccountCloseModal(true);
        setConfirmCloseModal(false);
    }

    //Add Account Continue Action
    const handleAddAccountContinue = () => {
        setAddAccountCloseModal(false);
        setSyncAddAccountOpen(true);
        setConfirmCloseModal(false);
    }

    const handleSuccessClose = () => {
        setSyncAddAccountOpen(false);
    }

    const syncToConsentData = {
        title: t('myBlockMonth.syncToCalendar.myACcessConsent'),
        descriptionGoogle: t('myBlockMonth.syncToCalendar.googleConsentModalMessage'),
        descriptioniOS: t('myBlockMonth.syncToCalendar.appleConsentModalMessage'),
        descriptionOutlook: t('myBlockMonth.syncToCalendar.outlookConsentModalMessage'),
        actionLabel: t('myBlockMonth.syncToCalendar.syncCancel'),
        handleAction: handleClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.syncContinue'),
        handleSecondaryAction: handleSyncContinueBtn,
        modalFlag: modalFlag,
        checked: checked,
        handleCheck: handleCheck,
    }

    const handleTokenError = () => {
        setAddAccountCloseModal(false);
        setSyncAddAccountOpen(false);
        resetAppleFields();
        resetIsGoogleSigned();
        resetIsOutlookSigned();
        data?.handleTokenError();
    }

    const syncAddAccountData = {
        actionLabel: t('myBlockMonth.syncToCalendar.syncCancel'),
        handleAction: handleAddAccountClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.addAccount'),
        modalFlag: modalFlag,
        emailId: emailId,
        shareEventsCol: shareEventsCol,
        setShareEventsCol: setShareEventsCol,
        operation: 'add',
        confirmCloseModal: confirmCloseModal,
        enableEmailAddress: enableEmailAddress,
        token: token,
        setSuccessMsgDetails: data.setSuccessMsgDetails,
        handleSuccessClose: handleSuccessClose,
        isSettingsScreen: data.settingsScreen,
        setSuccessMsgTimeOut: data.setSuccessMsgTimeOut,
        accountNickNames,
        availableEmailIds,
        handleTokenError: handleTokenError,
    }

    const resetAppleFields = () => {
        setEmailId('');
        setAppleSpecificPassword('');
        setEmailErrorMsg({ invalidError: false, alreadyExistsError: false });
        setPwdErrorMsg(false);
    }

    const handleFinalClose = () => {
        setAddAccountCloseModal(false);
        // to close the modals and reset
        setConfirmCloseModal(true);
        resetAppleFields();
        resetIsGoogleSigned();
        resetIsOutlookSigned();
        // clearing old token
        setToken('');
    }

    const validateAndSetEmail = (val) => {
        if (availableEmailIds['apple']?.includes(val?.trim())) setEmailErrorMsg({ invalidError: false, alreadyExistsError: true });
        else {
            setEmailErrorMsg({ invalidError: false, alreadyExistsError: false });
        }
        setEmailId(val);
    }

    const addAccountCloseModalData = {
        title: t('myBlockMonth.syncToCalendar.addAccountCloseTitle'),
        description: t('myBlockMonth.syncToCalendar.addAccountCloseDescription'),
        actionLabel: t('myBlockMonth.syncToCalendar.addAccountCloseCancel'),
        handleAction: handleFinalClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.addAccountCloseContinue'),
        handleSecondaryAction: handleAddAccountContinue,
    }

    const synciOSLoginOpenData = {
        title: t('myBlockMonth.syncToCalendar.syncToIOS'),
        description: t('myBlockMonth.syncToCalendar.syncToIOSText1'),
        descriptionSub: t('myBlockMonth.syncToCalendar.syncToIOSLearnHow'),
        actionLabel: t('myBlockMonth.syncToCalendar.syncCancel'),
        handleAction: handleClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.syncToIOSAuthorize'),
        handleSecondaryAction: handleAuthorizeRequestBtn,
        modalInstruction: false,
        setEmailId: validateAndSetEmail,
        setAppleSpecificPassword: setAppleSpecificPassword,
        emailId: emailId,
        appleSpecificPassword: appleSpecificPassword,
        emailErrorMsg: emailErrorMsg,
        pwdErrorMsg: pwdErrorMsg
    }

    const handleDialogClose = (event, reason) => {
        reason !== 'backdropClick' && onClose();
    }

    return <>
        {syncToInstructionOpen && <SyncDialogModal open={syncToInstructionOpen} onClose={handleClose} data={syncToInstructionData} />}
        {syncToConsentOpen && <SyncDialogModal open={syncToConsentOpen} onClose={handleClose} data={syncToConsentData} />}
        {addAccountCloseModal && <AcDialog open={addAccountCloseModal} onClose={handleClose} data={addAccountCloseModalData} />}
        {syncAddAccountOpen && <SyncAddAccountDialog open={syncAddAccountOpen} onClose={handleClose} data={syncAddAccountData} hideEditDialog={false} setHideEditDialog={() => {}}/>}
        {synciOSLoginOpen && <SyncDialogModal open={synciOSLoginOpen} onClose={handleClose} data={synciOSLoginOpenData} />}

        <Dialog
            open={open}
            onClose={handleDialogClose}
            className='sync-cal__dialog'
            maxWidth='lg'
            disableEscapeKeyDown={true}
        >
            <div className='sync-cal__dialog-header'>
                <IconButton
                    aria-label='close'
                    onClick={() => data.handleAction()}
                    className='sync-cal__dialog-close-btn'
                    size="large">
                    <CloseIcon fontSize='large' />
                </IconButton>
            </div>
            <DialogTitle>
                <div>
                    <span className="sync-cal__title-main">{data.titleMain}</span>
                    <h2 className="sync-cal__title-sub">{data.titleSub}</h2>
                </div>
            </DialogTitle>
            <DialogContent className='sync-cal__dialog-content'>                    
                {/* ODP-539 hiding apple stuff* /}
                {/* <div className='sync-cal__description-ios-content' onClick={() => openiOSInstructionPopUp()}>
                    <img className='sync-cal__dialog-ios-img' src={apple} alt='iOS' />
                    <p className='sync-cal__description-content-text sync-cal__description-ios-text'>{t('myBlockMonth.syncToCalendar.iosType')}</p>
                </div> */}
                <div className='sync-cal__description-content' onClick={() => openOutlookConsentPopUp()}>
                    <img className='sync-cal__dialog-outlook-img' src={outlook} alt='outlook' />
                    <p className='sync-cal__description-content-text'>{t('myBlockMonth.syncToCalendar.outlook')}</p>
                </div>
                <div className='sync-cal__description-content' onClick={() => openGoogleConsentPopUp()}>
                    <img className='sync-cal__dialog-google-img' src={google} alt='google' />
                    <p className='sync-cal__description-content-text'>{t('myBlockMonth.syncToCalendar.google')}</p>
                </div>
            </DialogContent>
            <DialogActions>
                <div className="sync-cal__buttons">
                    <Button disableRipple className='sync-cal__action-btn'><Link to="/how-to-sync-cal" target='_blank'>{t('myBlockMonth.syncToCalendar.howToSync')}</Link></Button>
                    {
                        !data.settingsScreen ?
                            <>
                                <span className='sync-cal__btn-section-divider' />
                                <Button disableRipple className='sync-cal__action-btn'> <Link to="/settings" target='_blank'>{t('myBlockMonth.syncToCalendar.manageAccounts')}</Link></Button>
                            </>
                            : ''
                    }
                </div>
            </DialogActions>
        </Dialog>
    </>;
};

export default SyncToCalDialog;